import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar } from "lucide-react";
import TopBar from "@/components/TopBar";
import { Activities, Deals, ActionItems as ActionItemsApi } from "@/api/endpoints";
import type { ActionItem } from "@/api/types";
import { fmtDayShort } from "@/lib/format";
import TimelineFilterBar, {
  EMPTY_FILTERS,
  type TimelineFilters,
} from "@/components/filters/TimelineFilterBar";
import ActivityCard from "@/components/ActivityCard";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { Segmented } from "@/components/ui";
import { todayISO } from "@/lib/people";

export default function ActivityPage() {
  const qc = useQueryClient();
  const todayStr = todayISO();
  const { ownerUserId, setOwnerUserId, openDeal } = useUIStore();
  // The owner filter is shared with the command palette ("their activity").
  const [baseFilters, setBaseFilters] = useState<TimelineFilters>(EMPTY_FILTERS);
  const filters: TimelineFilters = { ...baseFilters, ownerUserId };
  const setFilters = (f: TimelineFilters) => {
    setOwnerUserId(f.ownerUserId);
    setBaseFilters(f);
  };
  const [assignedToMe, setAssignedToMe] = useState(false);

  const user = useAuthStore((s) => s.user);
  const me = user?.id ?? "";

  const feedQuery = useQuery({
    queryKey: ["activity-feed", filters],
    queryFn: () =>
      Activities.feed({
        limit: 200,
        owner_user_id: filters.ownerUserId ?? undefined,
        account_id: filters.accountId ?? undefined,
        deal_id: filters.dealId ?? undefined,
        date_from: filters.date.from ?? undefined,
        date_to: filters.date.to ?? undefined,
      }),
  });

  const dealsQuery = useQuery({
    queryKey: ["deals", { all: true }],
    queryFn: () => Deals.list(),
  });

  const itemsQuery = useQuery({
    queryKey: ["action-items-feed"],
    queryFn: () => ActionItemsApi.feed(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["action-items-feed"] });
  };

  // Map deal_id → lead name for the cross-deal deal link header
  const dealsById = new Map((dealsQuery.data ?? []).map((d) => [d.id, d]));

  // Group action items by parent_activity_id
  const byActivity: Record<string, ActionItem[]> = {};
  for (const item of itemsQuery.data ?? []) {
    if (item.parent_activity_id) {
      if (!byActivity[item.parent_activity_id]) byActivity[item.parent_activity_id] = [];
      byActivity[item.parent_activity_id].push(item);
    }
  }

  // "Assigned to me" filter: keep activities that have at least one action item
  // assigned to the current user.
  const activities = (feedQuery.data ?? []).filter((a) => {
    if (!assignedToMe) return true;
    return (byActivity[a.id] ?? []).some((i) => i.assignee_user_id === me);
  });

  return (
    <>
      <TopBar title="Activity" subtitle="Everything logged across the deals you can see" />
      <div className="px-4 py-4 md:px-6 md:py-6">
        <div className="max-w-4xl panel p-5">
          <div className="mb-5">
            <Segmented
              label="Show"
              value={assignedToMe ? "mine" : "all"}
              onChange={(v) => setAssignedToMe(v === "mine")}
              options={[
                { value: "all", label: "All activity" },
                { value: "mine", label: "With tasks for me" },
              ]}
            />
          </div>

          {/* Filter bar */}
          <div className="flex items-start gap-3 flex-wrap mb-4">
            <TimelineFilterBar filters={filters} onChange={setFilters} />
          </div>

          {/* Timeline rail */}
          <ol className="relative ml-2 border-l border-fg/[0.08]">
            {activities.map((a) => {
              const deal = dealsById.get(a.deal_id);
              const dealName = deal?.lead ?? "Unknown deal";
              const accountName = deal?.account_name ?? null;

              if (a.type === "status_change") {
                // System event: compact muted marker — mirrors Timeline.tsx treatment
                return (
                  <li key={a.id} className="relative pl-6 py-1.5">
                    <span className="absolute -left-[5px] top-2.5 h-2 w-2 rounded-full bg-ink-900 ring-1 ring-fg/[0.12]" />
                    <div className="flex items-center gap-1.5 text-sm text-mut-500">
                      <Calendar className="w-3 h-3 shrink-0" />
                      {accountName && (
                        <>
                          <span className="text-mut-500 truncate">{accountName}</span>
                          <span className="text-mut-500">·</span>
                        </>
                      )}
                      <button
                        onClick={() => openDeal(a.deal_id)}
                        className="font-medium text-mut-300 hover:text-accent-azure truncate"
                      >
                        {dealName}
                      </button>
                      <span>·</span>
                      <span className="truncate">
                        {a.text ?? "Stage changed"} · {fmtDayShort(a.date)}
                      </span>
                    </div>
                  </li>
                );
              }

              // Content event: ActivityCard with a per-row deal-name header
              // The deal name link is rendered in Activity.tsx (not inside ActivityCard)
              // so ActivityCard stays deal-agnostic.
              return (
                <li key={a.id} className="relative pl-7 pb-4">
                  {/* Account + deal name link — cross-deal context header above the card */}
                  <div className="flex items-center gap-1.5 text-xs text-mut-500 mb-1 mt-1">
                    {accountName && (
                      <>
                        <span className="text-mut-300 truncate">{accountName}</span>
                        <span className="text-mut-500">·</span>
                      </>
                    )}
                    <button
                      onClick={() => openDeal(a.deal_id)}
                      className="font-medium text-mut-300 hover:text-accent-azure truncate"
                    >
                      {dealName}
                    </button>
                  </div>
                  <ActivityCard
                    activity={a}
                    items={byActivity[a.id] ?? []}
                    canEdit={false}
                    currentUserId={me}
                    todayStr={todayStr}
                    onMutated={invalidate}
                  />
                </li>
              );
            })}
            {activities.length === 0 && (
              <li className="pl-6 py-2 text-mut-300 text-md italic">
                {assignedToMe ? "No activities with items assigned to you." : "No activity yet."}
              </li>
            )}
          </ol>
        </div>
      </div>
    </>
  );
}
